import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Table,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Loader,
  Center,
  ColorSwatch,
} from "@mantine/core";
import { IconPlus, IconSearch, IconArrowLeft, IconLock } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAdminRoleListQuery } from "generated/graphql";

/**
 * RoleManagement (Admin)
 *
 * Lists all roles with search and navigation to edit/create.
 * Uses real API data from the AdminRoleList GraphQL query.
 */
export default function RoleManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [query, setQuery] = useState("");

  const { data, loading, error } = useAdminRoleListQuery({
    variables: {
      nameFilter: query.trim() || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  // Map API data to table format
  const roles = useMemo(() => {
    return (data?.roles ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      color: r.color,
      iconUrl: r.iconUrl,
      isSystem: r.isSystem,
    }));
  }, [data]);

  function onSelectRole(id: string) {
    navigate(`/admin-hub/roles/${id}`);
  }

  function onCreateRole() {
    navigate(`/admin-hub/roles/create`);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub")}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>
      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
          Role Management
        </Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          Manage roles and their associated permissions. System roles cannot be deleted.
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <TextInput
            placeholder="Search roles by name"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="w-full sm:max-w-xs"
          />
          <Button
            leftSection={<IconPlus />}
            className="bg-blue-500 hover:bg-blue-600"
            onClick={onCreateRole}
            size="sm"
          >
            Create Role
          </Button>
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Role</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th w={100}>Color</Table.Th>
                <Table.Th w={100}>Type</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && roles.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="red">
                      Failed to load roles.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : roles.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      {query
                        ? "No roles match your search."
                        : "No roles found. Create one to get started."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                roles.map((r) => (
                  <Table.Tr
                    key={r.id}
                    onClick={() => onSelectRole(r.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open role ${r.name}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onSelectRole(r.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Table.Td>
                      <Group gap="sm">
                        {r.isSystem && <IconLock size={14} color="gray" />}
                        <Text fw={500}>{r.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" lineClamp={1}>
                        {r.description || "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {r.color ? (
                        <Group gap="xs">
                          <ColorSwatch color={r.color} size={20} />
                          <Text size="xs" c="dimmed">
                            {r.color}
                          </Text>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {r.isSystem ? (
                        <Badge color="gray" variant="light">
                          System
                        </Badge>
                      ) : (
                        <Badge color="blue" variant="light">
                          Custom
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <Text
          size="sm"
          c={isDark ? "gray.5" : "gray.6"}
          className="text-center sm:text-left"
        >
          Showing {roles.length} role{roles.length === 1 ? "" : "s"}
        </Text>
      </div>
    </Container>
  );
}
